import React, { useEffect, memo, useMemo } from "react";
import { TileLayer, useMap } from "react-leaflet";

const MapTiles = ({ darkMode }) => {
  const map = useMap(); // Get access to the map instance
  
  // Memoize tile URLs to prevent recreation
  const lightTileUrl = useMemo(() => `https://{s}.google.com/vt/lyrs=m&x={x}&y={y}&z={z}&hl=en`, []);
  const darkTileUrl = useMemo(() =>
    "https://tile.jawg.io/jawg-dark/{z}/{x}/{y}{r}.png?access-token=sU2JxqeHLIuGEEAWX5p8BoK7LeMCdLeKgvXC6LCKryFZ1bXah2HUukA7ffczvusA", []);

  // Use a unique key based on the current mode to force re-render
  const tileUrl = darkMode ? darkTileUrl : lightTileUrl;
  const tileKey = darkMode ? "dark" : "light";
  
  // Handle tile loading issues - only when actually needed
  useEffect(() => {
    const handleTileError = (e) => {
      console.log("Tile error, attempting reload");
      setTimeout(() => {
        e.tile.src = e.tile.src + "?reload=" + new Date().getTime();
      }, 1000);
    };

    // Only add tile error handling - let the improved TileLayer settings handle the rest
    map.on('tileerror', handleTileError);
    
    return () => {
      map.off('tileerror', handleTileError);
    };
  }, [map]);

  // Memoize subdomains array to prevent recreation
  const subdomains = useMemo(() => darkMode ? [] : ["mt1", "mt2", "mt3"], [darkMode]);

  return (
    <TileLayer
      keepBuffer={4} // Moderate buffer to balance memory and performance
      updateWhenIdle={true} // Allow tiles to update when map is idle - fixes gray tiles
      updateWhenZooming={true} // Update tiles during zoom - fixes flyTo graying
      updateInterval={100} // Faster update interval for smoother loading
      key={tileKey} 
      url={tileUrl} 
      subdomains={subdomains}
      errorTileUrl="data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7" // Blank transparent image for error tiles
      className="tile-layer"
      maxZoom={22} // Maximum zoom for street-level detail
      minZoom={3} // Allow zooming out to continent level
      // Additional settings for better loading
      crossOrigin={true}
      loading="eager"
    />
  );
};

// Memoize MapTiles to prevent re-renders when parent Map component re-renders
const MemoizedMapTiles = memo(MapTiles, (prevProps, nextProps) => {
  // Only re-render if darkMode actually changed
  return prevProps.darkMode === nextProps.darkMode;
});

MemoizedMapTiles.displayName = 'MapTiles';

export default MemoizedMapTiles;
